import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Textarea } from "@/components/ui/textarea";
import { MapPin } from "lucide-react";

const AddressAutocomplete = ({ value, onChange, onBlur, language, onValidation }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const wrapperRef = useRef(null);

  const translations = {
    searching: {
      en: "Searching addresses...",
      ru: "Поиск адресов...",
      lv: "Meklē adreses..."
    },
    noResults: {
      en: "No addresses found",
      ru: "Адреса не найдены",
      lv: "Adreses nav atrastas"
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const searchAddresses = async (query) => {
    if (!query || query.length < 3) {
      setSuggestions([]);
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(
        "https://nominatim.openstreetmap.org/search",
        {
          params: {
            q: `${query}, Liepāja, Latvia`,
            format: "json",
            limit: 5,
            addressdetails: 1,
            countrycodes: "lv",
            "accept-language": "lv"
          },
          headers: {
            "User-Agent": "RozeDiNapoli/1.0"
          }
        }
      );

      if (response.data && response.data.length > 0) {
        setSuggestions(response.data);
        setShowSuggestions(true);
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    } catch (error) {
      console.error("Address search error:", error);
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    onChange(newValue);
    
    if (newValue.length >= 3) {
      searchAddresses(newValue);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSelectSuggestion = (suggestion) => {
    const address = suggestion.display_name;
    onChange(address);
    setSuggestions([]);
    setShowSuggestions(false);
    
    // Trigger validation with the selected address and its coordinates
    if (onValidation) {
      onValidation(address, { lat: parseFloat(suggestion.lat), lon: parseFloat(suggestion.lon) });
    }
  };

  const formatAddress = (suggestion) => {
    const address = suggestion.address;
    const parts = [];
    
    if (address.road) parts.push(address.road);
    if (address.house_number) parts.push(address.house_number);
    if (address.suburb) parts.push(address.suburb);
    
    return parts.join(", ") || suggestion.display_name;
  };

  return (
    <div ref={wrapperRef} className="relative">
      <Textarea
        value={value}
        onChange={handleInputChange}
        onBlur={onBlur}
        placeholder="Street, house number, Liepāja"
        rows={3}
        data-testid="address-input"
        className="w-full"
      />
      
      {loading && (
        <div className="absolute top-full left-0 right-0 mt-1 p-2 bg-white border rounded-md shadow-lg z-50">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
            <span>{translations.searching[language]}</span>
          </div>
        </div>
      )}
      
      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border rounded-md shadow-lg z-50 max-h-60 overflow-y-auto">
          {suggestions.map((suggestion, index) => (
            <div
              key={index}
              onClick={() => handleSelectSuggestion(suggestion)}
              className="p-3 hover:bg-gray-100 cursor-pointer border-b last:border-b-0 transition-colors"
            >
              <div className="flex items-start gap-2">
                <MapPin className="w-4 h-4 text-red-600 mt-1 flex-shrink-0" />
                <div className="flex-1">
                  <div className="font-medium text-sm">{formatAddress(suggestion)}</div>
                  <div className="text-xs text-gray-500 mt-1">
                    {suggestion.address.city || suggestion.address.town || "Liepāja"}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      
      {showSuggestions && suggestions.length === 0 && !loading && value.length >= 3 && (
        <div className="absolute top-full left-0 right-0 mt-1 p-3 bg-white border rounded-md shadow-lg z-50 text-sm text-gray-500">
          {translations.noResults[language]}
        </div>
      )}
    </div>
  );
};

export default AddressAutocomplete;
